import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceItemsApi } from '../evidenceApi';
import type { EvidenceContainerGroup } from '../evidenceTypes';
import { useStaffOptions } from '../../../human-resources/personnel/useStaff';

const ID = 'evidence-container';

/**
 * A CONTAINER's info, from its header in the Stored in folder — product call, 08 Oct 2026: *Edit:
 * displays / edit the container info: custodian and agent*. Written to every stored item in the box.
 * Opened by the box's custodian only (the server refuses anyone else).
 *
 * A new custodian is a HAND-OVER: each item's chain of custody gets a line recording it. The form
 * says so before the tick, because it does more than its label suggests.
 */
export function EvidenceContainerModal({
  caseId,
  container,
  itemCount,
  onClose,
  onSaved,
}: {
  caseId: string;
  container: EvidenceContainerGroup;
  itemCount: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);

  const [custodianId, setCustodianId] = useState(container.custodianPersonnelId ?? '');
  const [agentId, setAgentId] = useState(container.submittingAgentPersonnelId ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const gap = custodianId === '' ? 'Choose the custodian.' : agentId === '' ? 'Choose the agent.' : null;
  const canSave = gap === null && confirmed && !saving;
  const handsOver = custodianId !== '' && custodianId !== container.custodianPersonnelId;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await evidenceItemsApi.updateContainer({
        evidenceCaseId: caseId,
        containerNumber: container.containerNumber,
        custodianPersonnelId: custodianId,
        submittingAgentPersonnelId: agentId,
      });
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the container.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit container ${container.containerNumber ?? '(no container)'}`}
      size="lg"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => {
          if (staff.length === 0) return;
          setCustodianId(staff[Math.floor(Math.random() * staff.length)].value);
          setAgentId(staff[Math.floor(Math.random() * staff.length)].value);
        },
        disabled: saving || loadingStaff,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={gap}>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id={`${ID}-container-number`}
          label="Container/Box No"
          description="The box being edited. Its number is not changed here."
          value={container.containerNumber ?? 'No container'}
          readOnly
          disabled
          className="font-mono"
        />

        <Select
          id={`${ID}-custodian-personnel-id`}
          label="Custodian"
          required
          description="The employee holding the box. Choosing someone else hands every item in it over to them."
          placeholder="Choose an employee"
          value={custodianId}
          onChange={setCustodianId}
          options={staff}
          remote
          loading={loadingStaff}
          disabled={saving}
          error={error?.fieldError('custodianPersonnelId')}
        />

        <Select
          id={`${ID}-submitting-agent-personnel-id`}
          label="Agent"
          required
          description="The agent who submitted the evidence in this box."
          placeholder="Choose an employee"
          value={agentId}
          onChange={setAgentId}
          options={staff}
          remote
          loading={loadingStaff}
          disabled={saving}
          error={error?.fieldError('submittingAgentPersonnelId')}
        />

        <p className="text-xs text-slate-500">
          Applies to {itemCount === 1 ? 'the 1 stored item' : `all ${itemCount} stored items`} in this box.
          {handsOver
            ? ' Handing it to another custodian writes a line on each item’s chain of custody, and you will no longer hold the box.'
            : ''}
        </p>
      </div>
    </Modal>
  );
}
